// Login — Google sign-in surface. Reads ?next= (post-login destination) and
// ?intent=sync (signup → Cloud Sync checkout) to tailor the copy.
import { GoogleLogo, ArrowRight, CloudArrowUp } from "@phosphor-icons/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/dashboard";
  const syncIntent = params.get("intent") === "sync";

  // If already signed in, bounce straight to the destination.
  useEffect(() => {
    if (!loading && user) navigate(next, { replace: true });
  }, [loading, user, navigate, next]);

  return (
    <div className="min-h-[80dvh] grid place-items-center px-4 py-12" data-testid="login-page">
      <div className="border-2 border-black p-6 max-w-md w-full space-y-6 bg-white">
        <div className="space-y-2">
          <div className="label-mono text-xs text-neutral-500">AIIMAGESUITES · CAPTURE</div>
          <h1 className="font-display font-black tracking-tighter text-3xl" data-testid="login-headline">
            {syncIntent ? "CREATE YOUR ACCOUNT" : "SIGN IN"}
          </h1>
          <p className="text-sm">
            {syncIntent ? (
              <>Free Google sign-in first, then unlock <b>Cloud Sync</b> ($12/mo, cancel anytime) to deliver batches straight into <span className="font-mono">aiimagesuite.pro</span>. Capturing stays free.</>
            ) : (
              <>A free Google sign-in keeps your sessions private to you and syncs them across devices.</>
            )}
          </p>
        </div>

        <button
          onClick={() => login(next)}
          data-testid="login-google-btn"
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-black text-white py-3 font-display tracking-wider hover:bg-neutral-900 active:scale-[0.98]"
        >
          <GoogleLogo size={18} weight="bold" />
          CONTINUE WITH GOOGLE
        </button>

        {syncIntent && (
          <div className="border-2 border-[#002FA7] p-3 flex items-start gap-2 text-xs font-mono" data-testid="login-sync-note">
            <CloudArrowUp size={16} weight="bold" className="text-[#002FA7] shrink-0 mt-0.5" />
            After sign-in you land on the Cloud Sync page with Stripe checkout ready. You can skip it and keep capturing for free.
          </div>
        )}

        <div className="label-mono text-[10px] text-neutral-500 space-y-1">
          <div>· No password to remember · free forever to capture</div>
          <div>· Session lasts 7 days (httpOnly cookie)</div>
          <div>· Sign out anytime from the header</div>
        </div>

        <div className="border-t border-neutral-300 pt-3 flex items-center justify-between text-sm">
          <Link to="/pricing" className="label-mono text-xs text-neutral-500 hover:text-black" data-testid="login-pricing-link">
            SEE PRICING
          </Link>
          <Link to="/" className="label-mono text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid="login-home-link">
            BACK TO HOME <ArrowRight size={14} weight="bold" className="text-neutral-400" />
          </Link>
        </div>
      </div>
    </div>
  );
}
